import React from "react";
import { Avatar, Divider, Drawer, IconButton, ListItemIcon } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { AccountCircleOutlined, ExitToAppOutlined, LockOutlined, NotificationsNoneOutlined, SettingsOutlined } from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import Notifications from "./Notifications";
import pathnames from "../../constants/pathnames";
import useUserData from "../../hooks/useUserData";
import Logo from "../../lib/assets/images/jureb-logo.png";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import OrganizationIcon from "../assets/icons/OrganizationIcon";
import UsersIcon from "../assets/icons/Users";
import PromoCodeIcon from "../assets/icons/PromoCodeIcon";
import IntegrationsIcon from "../assets/icons/IntegrationsIcon";
import MenuIcon from "@mui/icons-material/Menu";
import BillCheckIcon from "../assets/icons/BillCheck";
import PushNotificationIcon from "../assets/icons/PushNotificationIcon";
import AdminUserIcon from "../assets/icons/AdminUserIcon";
import MarketplaceIcon from "../assets/icons/MarketplaceIcon";
import SidebarCollapsibleItem, { isGroupActive } from "./Layout/SidebarCollapsibleItem";
import { isMarketplaceEnabled } from "../../config";

const Header = ({ title, description }) => {
  const location = useLocation();
  const pathname = location.pathname;
  const { user } = useUserData();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [isNotification, setIsNotification] = React.useState(null);
  const [isOpen, setIsOpen] = React.useState(false);

  const SIDEBAR_LINKS = [
    {
      name: "Overview",
      path: pathnames.DASHBOARD,
      Icon: GridViewOutlinedIcon,
    },
    {
      name: "Organization Management",
      path: pathnames.ORGANIZATIONS,
      Icon: OrganizationIcon,
      children: [
        { name: "Businesses", path: pathnames.ORGANIZATIONS },
        { name: "Users", path: pathnames.USERS },
        { name: "Payments", path: pathnames.PAYMENTS },
      ],
    },
    {
      name: "Referral Management",
      path: pathnames.REFERRERS,
      Icon: UsersIcon,
      children: [
        { name: "All Referrals", path: pathnames.REFERRERS },
        { name: "Payouts", path: pathnames.PAYOUTS },
      ],
    },
    {
      name: "Promo Code Management",
      path: pathnames.PROMO_CODES,
      Icon: PromoCodeIcon,
      children: [
        { name: "Overview", path: pathnames.PROMO_CODES_OVERVIEW },
        { name: "Redemptions", path: pathnames.PROMO_CODES_REDEMPTIONS },
      ],
    },
    {
      name: "Marketplace",
      path: pathnames.MARKETPLACE,
      Icon: MarketplaceIcon,
      children: [
        { name: "Add-ons", path: pathnames.MARKETPLACE_ADDONS },
        { name: "Payments", path: pathnames.MARKETPLACE_PAYMENTS },
      ],
    },
    {
      name: "Integrations",
      path: pathnames.INTERFACES,
      Icon: IntegrationsIcon,
    },
    {
      name: "FIRS Transmissions",
      path: pathnames.TRANSMISSIONS,
      Icon: BillCheckIcon,
      children: [
        { name: "All Transmissions", path: pathnames.TRANSMISSIONS, matches: [pathnames.API_TRANSMISSIONS] },
        { name: "Unassigned Invoices", path: pathnames.UNASSIGNED_INVOICES },
      ],
    },
    {
      name: "Push Notification",
      path: pathnames.PUSH_NOTIFICATIONS,
      Icon: PushNotificationIcon,
    },
  ].filter((item) => isMarketplaceEnabled || item.path !== pathnames.MARKETPLACE);

  const SIDEBAR_LINKS_II = [
    {
      name: "Administrators",
      path: pathnames.ADMINISTRATORS,
      Icon: AdminUserIcon,
    },
    {
      name: "Settings",
      path: pathnames.SETTINGS_PROFILE,
      Icon: SettingsOutlinedIcon,
    },
  ];
  // Only one dropdown may be open at a time; default to the group containing the current route
  const activeGroup = SIDEBAR_LINKS.find((item) => item.children && isGroupActive(item, pathname))?.name ?? null;
  const [openGroup, setOpenGroup] = React.useState(activeGroup);

  React.useEffect(() => {
    if (activeGroup) setOpenGroup(activeGroup);
  }, [activeGroup]);

  return (
    <div className="border-bottom p-3 sticky-top bg-white">
      <nav className="d-flex align-items-center justify-content-between">
        <div>
          <div className="d-flex align-items-center gap-4">
            <IconButton onClick={toggleDrawer} className="d-lg-none">
              <MenuIcon />
            </IconButton>
            <div>
              <div className="fw-medium fs-4">{title}</div>
              <div className="d-none d-md-block">{description}</div>
            </div>
          </div>
        </div>
        <div className="d-flex gap-3 align-items-center">
          <div>
            <IconButton onClick={(e) => setIsNotification(e.currentTarget)}>
              <NotificationsNoneOutlined />
            </IconButton>
          </div>
          <Link to={pathnames.SETTINGS_PROFILE} className="d-flex gap-2 align-items-center">
            <Avatar src={user?.avatarUploadMeta?.url} className="bg-primary">
              {user?.firstName?.charAt(0)}
              {user?.lastName?.charAt(0)}
            </Avatar>
            <div className="text-dark text-nowrap">
              {user?.firstName} {user?.lastName?.charAt(0)}.
            </div>
          </Link>
          <div>
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
              <KeyboardArrowDownIcon />
            </IconButton>
          </div>
        </div>
      </nav>

      <Menu id="basic-menu" anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem disabled>Account Settings</MenuItem>
        <MenuItem>
          <Link to={pathnames.SETTINGS_PROFILE} className="text-dark d-flex align-items-center">
            <ListItemIcon>
              <AccountCircleOutlined />
            </ListItemIcon>
            Profile
          </Link>
        </MenuItem>
        <MenuItem>
          <Link to={pathnames.SETTINGS_TAX_RATES} className="text-dark d-flex align-items-center">
            <ListItemIcon>
              <SettingsOutlined />
            </ListItemIcon>
            Settings
          </Link>
        </MenuItem>
        <MenuItem>
          <Link to={pathnames.SETTINGS_CHANGE_PASSWORD} className="text-dark d-flex align-items-center">
            <ListItemIcon>
              <LockOutlined />
            </ListItemIcon>
            Password and Security
          </Link>
        </MenuItem>
        <Divider />
        <MenuItem>
          <Link to={pathnames.SIGN_OUT} className="text-dark d-flex align-items-center">
            <ListItemIcon>
              <ExitToAppOutlined />
            </ListItemIcon>
            Logout
          </Link>
        </MenuItem>
      </Menu>
      <Notifications anchorEl={isNotification} onClose={() => setIsNotification(null)} />
      <Drawer anchor={"left"} open={isOpen} onClose={() => setIsOpen(false)}>
        <div style={{ width: 290, height: "100vh", overflowY: "auto" }}>
          <div className="p-3" style={{ minHeight: "calc(100vh - 100px)" }}>
            <div className="mb-4 d-flex justify-content-center align-items-center">
              <img src={Logo} alt="Jureb" />
            </div>
            <div className="d-flex flex-column justify-content-between gap-4 h-100">
              <div className="">
                <div className="text-secondary fs-xs mb-3 fw-medium">MAIN MENU</div>
                {SIDEBAR_LINKS.map(({ Icon, ...item }, index) => {
                  if (item?.children) {
                    return (
                      <SidebarCollapsibleItem
                        key={index}
                        item={{ Icon, ...item }}
                        pathname={pathname}
                        isOpen={openGroup === item.name}
                        onToggle={() => setOpenGroup((prev) => (prev === item.name ? null : item.name))}
                        onNavigate={() => setIsOpen(false)}
                      />
                    );
                  }

                  const isLinkActive = item?.path === pathnames.DASHBOARD
                    ? pathname === pathnames.DASHBOARD || pathname === "/"
                    : item?.path && (pathname === item.path || (item.path !== "/" && pathname.startsWith(item.path + "/")));

                  return (
                    <div key={index} style={{ marginBottom: "2px" }}>
                      <Link
                        to={item?.path}
                        onClick={() => setIsOpen(false)}
                        className="d-flex align-items-center gap-3 px-2 py-2 text-decoration-none"
                        style={{
                          borderRadius: "4px",
                          transition: "all 0.15s ease",
                          color: isLinkActive ? "#0F172A" : "#343A40",
                          fontWeight: isLinkActive ? 700 : 500,
                          fontSize: "14px",
                          fontFamily: "Rubik, sans-serif",
                        }}
                        onMouseEnter={(e) => {
                          if (!isLinkActive) e.currentTarget.style.color = "#0F172A";
                        }}
                        onMouseLeave={(e) => {
                          if (!isLinkActive) e.currentTarget.style.color = "#343A40";
                        }}
                      >
                        <span style={{ display: "flex", alignItems: "center", color: isLinkActive ? "#0F172A" : "#343A40" }}>
                          <Icon />
                        </span>
                        <span className="text-nowrap">{item?.name}</span>
                      </Link>
                    </div>
                  );
                })}
              </div>
              <div>
                <div className="text-secondary fs-xs mb-3 fw-medium">PREFERENCES</div>
                {SIDEBAR_LINKS_II.map(({ Icon, ...item }, index) => {
                  const isLinkActive = item?.path === pathnames.SETTINGS_PROFILE
                    ? pathname.startsWith("/settings")
                    : pathname && (pathname === item.path || (item.path !== "/" && pathname.startsWith(item.path + "/")));

                  return (
                    <div key={index} style={{ marginBottom: "2px" }}>
                      <Link
                        to={item?.path}
                        onClick={() => setIsOpen(false)}
                        className="d-flex align-items-center gap-3 px-2 py-2 text-decoration-none"
                        style={{
                          borderRadius: "4px",
                          transition: "all 0.15s ease",
                          color: isLinkActive ? "#0F172A" : "#343A40",
                          fontWeight: isLinkActive ? 700 : 500,
                          fontSize: "14px",
                          fontFamily: "Rubik, sans-serif",
                        }}
                        onMouseEnter={(e) => {
                          if (!isLinkActive) e.currentTarget.style.color = "#0F172A";
                        }}
                        onMouseLeave={(e) => {
                          if (!isLinkActive) e.currentTarget.style.color = "#343A40";
                        }}
                      >
                        <span style={{ display: "flex", alignItems: "center", color: isLinkActive ? "#0F172A" : "#343A40" }}>
                          <Icon />
                        </span>
                        <span className="text-nowrap">{item?.name}</span>
                      </Link>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );

  function toggleDrawer(event) {
    if (event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) {
      return;
    }
    setIsOpen(true);
  }
};

export default Header;
